import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, Inject, Input } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { BehaviorSubject } from 'rxjs';
import { normalizeSearchText } from 'src/app/core/_utils/utils.service';

export class ItemFlatNode {
  displayName?: string;
  level?: number;
  resourceId?: string;
  expandable?: boolean;
  code?: string;
  ceus?: [];
  detailedResource?: boolean;
  workUnit?: string;
  workUnityId?: string;
  ownerId?: string;
  resourceType?: string;
  ruade?: boolean;
  fileName?: string;
  fCeuFileName?: string;
  justified?: boolean;
  pipeProduct?: number;
  cableProduct?: boolean;
  possibleEnergies?: string[];
  energyId?: string;
  transportId?: string;
  isComplexProduct?: boolean;
  modalityName?: string;
  modalityId?: string;
}

export class ItemNode {
  children!: ItemNode[];
  displayName!: string;
  resourceId?: string;
  code?: string;
  ceus?: [];
  detailedResource?: boolean;
  workUnit?: string;
  workUnityId?: string;
  ownerId?: string;
  resourceType?: string;
  ruade?: boolean;
  fileName?: string;
  fCeuFileName?: string;
  justified?: boolean;
  pipeProduct?: number;
  cableProduct?: boolean;
  possibleEnergies?: string[];
  energyId?: string;
  transportId?: string;
  isComplexProduct?: boolean;
  modalityName?: string;
  modalityId?: string;
}

@Component({
  selector: 'app-dialog-add-composition',
  templateUrl: './dialog-add-composition.component.html',
  styleUrls: ['./dialog-add-composition.component.scss']
})
export class DialogAddCompositionComponent {
  flatNodeMap = new Map<ItemFlatNode, ItemNode>();
  nestedNodeMap = new Map<ItemNode, ItemFlatNode>();
  selectedParent: ItemFlatNode | null = null;
  treeControl: FlatTreeControl<ItemFlatNode>;
  treeFlattener!: MatTreeFlattener<ItemNode, ItemFlatNode>;
  dataSource!: MatTreeFlatDataSource<ItemNode, ItemFlatNode>;
  checklistSelection = new SelectionModel<ItemFlatNode>(true);
  dataChange = new BehaviorSubject<ItemNode[]>([]);
  dataService: ItemNode[] = [];
  showOnlyFavorites: boolean = false;
  showFullDatabase: boolean = false;
  refItems!: any[];
  @Input() editMode: boolean = false;
  @Input() isSingular: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<DialogAddCompositionComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    if (data) {
      this.editMode = data.editMode || false;
      this.isSingular = data.isSingular || false;
      if (data.filterEmptyParents && Array.isArray(data.fullTree)) {
        data.fullTree = data.fullTree.filter((x: { value: any[] }) => x.value?.some((y: { value: any[] }) => y.value?.some((z: { displayName: any }) => z.displayName)));
      }
      this.initialize(data.fullTree);
    }

    this.checklistSelection = new SelectionModel<ItemFlatNode>(this.isSingular ? false : true);

    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel, this.isExpandable, this.getChildren);
    this.treeControl = new FlatTreeControl<ItemFlatNode>(this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    this.dataChange.subscribe((data) => {
      this.dataSource.data = data;
    });
  }

  initialize(data: any) {
    this.dataService = this.buildFileTree(data);
    this.refItems = data;
    // Notify the change.
    this.dataChange.next(this.dataService);
  }

  getLevel = (node: ItemFlatNode) => node.level || 0;

  getChildren = (node: ItemNode): any[] => node.children;

  hasNoContent = (_: number, _nodeData: ItemFlatNode) => _nodeData.displayName === '';

  //get dataIems(): ItemNode[] { return this.dataChange.value; }

  onClickBack() {
    this.dialogRef.close();
  }

  onClickAdd() {
    return this.checklistSelection?.selected.filter((x) => x.resourceId != null);
  }

  nameChanged(target: any) {
    if (this.dataService.length > 0 && target?.value?.length < 2) {
      this.dataChange.next(this.dataService);
    }

    let filteredData: any[] = [];

    if (target?.value?.length < 2) {
      this.dataChange.next(this.dataService);
      return;
    }

    const searchValue = normalizeSearchText(target?.value);
    let filteredParents: any[] = [];

    filteredParents = this.dataService.filter(
      (c) =>
        normalizeSearchText(c.displayName).includes(searchValue) ||
        normalizeSearchText(c.code).includes(searchValue) ||
        c.children.some(
          (x) =>
            normalizeSearchText(x.displayName).includes(searchValue) ||
            normalizeSearchText(x.code).includes(searchValue) ||
            x.children?.some(
              (y) =>
                normalizeSearchText(y.displayName).includes(searchValue) ||
                normalizeSearchText(y.code).includes(searchValue) ||
                y.children?.some((z) => normalizeSearchText(z.displayName).includes(searchValue) || normalizeSearchText(z.code).includes(searchValue))
            )
        )
    );

    filteredParents.forEach((fP) => {
      let filteredChildrenValue: any[] = [];

      let filteredChildren = fP.children.filter(
        (v: { displayName: string; code: string; children: { displayName: string; code: string }[] }) =>
          normalizeSearchText(v.displayName).includes(searchValue) ||
          normalizeSearchText(v.code).includes(searchValue) ||
          v.children?.some((y: { displayName: string; code: string }) => normalizeSearchText(y.displayName).includes(searchValue) || normalizeSearchText(y.code).includes(searchValue))
      );

      filteredChildren.forEach((fC: any) => {
        let filteredChildrenNext = fC.children?.filter((z: { displayName: string; code: string }) => normalizeSearchText(z.displayName).includes(searchValue) || normalizeSearchText(z.code).includes(searchValue));

        let item = {
          ...fC,
          children: filteredChildrenNext
        };

        filteredChildrenValue.push(item);
      });

      let item = {
        ...fP,
        children: filteredChildrenValue
      };

      filteredData.push(item);
    });

    this.dataChange.next(filteredData);
    this.treeControl.expandAll();
  }

  favoriteToggle() {
    if (this.showOnlyFavorites) {
      this.initialize(this.data.favoriteData);
    } else if (this.showFullDatabase) {
      this.initialize(this.data.fullDatabaseTree);
    } else {
      this.initialize(this.data.fullTree);
    }
  }

  fullDatabaseToggle() {
    this.showOnlyFavorites = false;
    this.showFullDatabase ? this.initialize(this.data.fullDatabaseTree) : this.initialize(this.data.fullTree);
  }

  isExpandable = (node: ItemFlatNode) => node.expandable || false;

  transformer = (node: ItemNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.displayName === node.displayName ? existingNode : new ItemFlatNode();
    flatNode.displayName = node.displayName;
    flatNode.level = level;
    flatNode.resourceId = node.resourceId != node.displayName ? node.resourceId : undefined;
    flatNode.code = node.code;
    flatNode.detailedResource = node.detailedResource;
    flatNode.ceus = node?.ceus;
    flatNode.workUnit = node?.workUnit;
    flatNode.workUnityId = node?.workUnityId;
    flatNode.expandable = !!node.children?.length;
    flatNode.ownerId = node.ownerId;
    flatNode.resourceType = node.resourceType;
    flatNode.ruade = node.ruade;
    flatNode.fCeuFileName = node.fCeuFileName;
    flatNode.fileName = node.fileName;
    flatNode.justified = node.justified;
    flatNode.pipeProduct = node.pipeProduct;
    flatNode.cableProduct = node.cableProduct;
    flatNode.possibleEnergies = node.possibleEnergies;
    flatNode.energyId = node.energyId;
    flatNode.transportId = node.transportId;
    flatNode.isComplexProduct = node.isComplexProduct;
    flatNode.modalityName = node.modalityName;
    flatNode.modalityId = node.modalityId;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };

  leafItemSelectionToggle(node: ItemFlatNode): void {
    if (this.isSingular) {
      this.checklistSelection.clear();
      this.checklistSelection.toggle(node);
    } else {
      this.checklistSelection.toggle(node);
      this.checkAllParentsSelection(node);
    }
  }

  checkAllParentsSelection(node: ItemFlatNode): void {
    let parent: ItemFlatNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
  }

  checkRootNodeSelection(node: ItemFlatNode): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected =
      descendants.length > 0 &&
      descendants.every((child) => {
        return this.checklistSelection.isSelected(child);
      });
    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node);
    }
  }

  getParentNode(node: ItemFlatNode): ItemFlatNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel && currentLevel < 1) {
      return null;
    }

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (currentLevel && this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  descendantsAllSelected(node: ItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected =
      descendants.length > 0 &&
      descendants.every((child) => {
        return this.checklistSelection.isSelected(child);
      });
    return descAllSelected;
  }

  descendantsPartiallySelected(node: ItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some((child) => this.checklistSelection.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  itemSelectionToggle(node: ItemFlatNode): void {
    if (this.isSingular) {
      this.checklistSelection.clear();
      this.checklistSelection.toggle(node);
    } else {
      this.checklistSelection.toggle(node);
      const descendants = this.treeControl.getDescendants(node);
      this.checklistSelection.isSelected(node) ? this.checklistSelection.select(...descendants) : this.checklistSelection.deselect(...descendants);

      descendants.forEach((child) => this.checklistSelection.isSelected(child));
      this.checkAllParentsSelection(node);
    }
  }

  hasChild = (_: number, _nodeData: ItemFlatNode) => _nodeData.expandable;

  buildFileTree(obj: { [key: string]: any }, level: number = 0): ItemNode[] {
    return Object.keys(obj).reduce<ItemNode[]>((accumulator, key) => {
      const value = obj[key];
      const node = new ItemNode();

      if (value.value != null && level == 0) {
        node.displayName = value.key;
        if (typeof value === 'object') {
          const ownGroups = value.value.filter((v: { key: string | null }) => v.key == null || v.key === value.key);
          const industryGroups = value.value.filter((v: { key: string | null }) => v.key != null && v.key !== value.key);
          node.children = ownGroups.reduce((children: ItemNode[], group: { value: any }) => [...children, ...this.buildFileTree(group.value, level + 1)], []);
          if (industryGroups.length > 0) {
            node.children = [...node.children, ...this.buildFileTree(industryGroups, level + 1)];
          }
        }
      } else if (level == 1 && value.key != null) {
        node.displayName = value.key;
        node.children = this.buildFileTree(value.value, level + 1);
      } else if (value != null && level == 0) {
        node.displayName = key;
        if (typeof value === 'object') {
          node.children = this.buildFileTree(value, level + 1);
        }
      } else {
        node.resourceId = value?.resourceId;
        node.displayName = value?.displayName;
        node.code = value?.code;
        node.detailedResource = value?.detailedResource;
        node.resourceType = value?.resourceType;
        node.ruade = value?.ruade;
        node.fCeuFileName = value?.fCeuFileName;
        node.fileName = value?.fileName;
        node.justified = value?.justified;
        node.pipeProduct = value?.pipeProduct;
        node.cableProduct = value?.cableProduct;
        node.possibleEnergies = value?.possibleEnergies;
        node.energyId = value?.energyId;
        node.transportId = value?.transportId;
        node.isComplexProduct = value?.isComplexProduct;
        node.modalityName = value?.modalityName;
        node.modalityId = value?.modalityId;
        if (value?.ceus) {
          node.ceus = value?.ceus;
        }
        if (value?.workUnit) {
          node.workUnit = value?.workUnit;
        }
        if (value?.workUnityId) {
          node.workUnityId = value?.workUnityId;
        }
        if (value?.ownerId) {
          node.ownerId = value?.ownerId;
        }
      }

      return accumulator.concat(node);
    }, []);
  }
}
